/* eslint-disable camelcase */
import Intercom from "@intercom/messenger-js-sdk";
import QueryString from "query-string";
import {
  all,
  call,
  put,
  select,
  takeEvery,
  takeLatest,
} from "redux-saga/effects";
import api from "../../utils/api";
import { generateHmac } from "../../utils/crypto";
import { clearFeedViewTracking } from "../../utils/feedViewStorage";
import {
  allFeedAccess,
  overwritePerms,
  populateDefLocation,
  populateList,
} from "../../utils/helpers";
import { errorAlert, successAlert } from "../Alerts/actions";
import { setFeedFilter } from "../Feed/actions";
import {
  setActiveLocation,
  setActiveSurvey,
  setUserLocations,
} from "../SlickCareProfiles/actions";
import actions from "./actions";
import {
  CHECK,
  CHECK_EMAIL_SUB,
  FORGOT_PASSWORD,
  GET_APP_QR,
  GET_MENU_INDICATORS,
  GET_SUBLIST,
  GET_USERDATA,
  LOGIN,
  LOGOUT,
  RESET_PASSWORD,
  SENT_RESET_PASSWORD,
  SET_PASSWORD,
  UPDATE_EMAIL_SUB,
  UPDATE_USER,
} from "./constants";
import { selectToken } from "./selectors";
const {
  checkFailure,
  checkSuccess,
  loginFailure,
  loginSuccess,
  logoutFailure,
  sentResetLinkSuccess,
  sentResetLinkFailure,
  forgotPasswordSuccess,
  forgotPasswordFailure,
  resetPasswordSuccess,
  resetPasswordFailure,
  getUserDataSuccess,
  getUserDataFailure,
  getSubList: getSubListAction,
  getSubListSuccess,
  getSubListFailure,
  updateUserSuccess,
  updateUserFailure,
  setPasswordSuccess,
  setPasswordFailure,
  updateAllFeedAccess,
  checkEmailSubSuccess,
  checkEmailSubFailure,
  updateEmailSubSuccess,
  updateEmailSubFailure,
  getAppQRSuccess,
  getAppQRFailure,
  getMenuIndicators: getMenuIndicatorsAction,
  getMenuIndicatorsSuccess,
  getMenuIndicatorsFailure,
} = actions;

export function* checkAuth({ token, history }) {
  if (token) {
    try {
      const userInfo = yield call(api(token).post, `api/employee/me`);
      const activeLocation = yield select(
        (state) => state?.SlickCareProfilesReducer?.activeLocation,
      );

      if (userInfo instanceof Error) throw userInfo;

      // Use the latest token from Redux - sliding window refresh may have
      // replaced the original token during the API call above
      const latestToken = yield select(selectToken);

      let user = overwritePerms(userInfo.data);
      let locationList = populateList(user);
      let location = populateDefLocation(user);
      yield put(setUserLocations(locationList ?? []));
      if (activeLocation?.id === undefined) {
        yield put(setActiveLocation(location ?? {}));
      }
      yield put(setActiveSurvey(location ?? {}));
      yield put(getSubListAction());
      yield put(checkSuccess({ token: latestToken || token, user: user }));
      yield put(getMenuIndicatorsAction());

      let hasAllFeedAccess = allFeedAccess(user?.location);
      yield put(updateAllFeedAccess(hasAllFeedAccess));
      if (!hasAllFeedAccess) {
        yield put(
          setFeedFilter({
            target: "location",
            page: 1,
            limit: 10,
          }),
        );
      }

      if (history.location.pathname === "/")
        history.replace(
          history.location.state && history.location.state.referrer
            ? history.location.state.referrer.pathname
            : `/d/home`,
        );
    } catch (e) {
      yield put(checkFailure(e));
      localStorage.clear();
      if (history.location.pathname !== "/") history.replace("/");
    }
  } else yield put(checkFailure("Invalid token"));
}

export function* updateUserData({ onUpdateSuccess, payload }) {
  const token = yield select(selectToken);
  try {
    const userInfo = yield call(api(token).post, `api/employee/me`);
    if (userInfo instanceof Error) throw userInfo;
    const latestToken = yield select(selectToken);
    let locationList = populateList(userInfo?.data);
    let location = populateDefLocation(userInfo?.data);
    yield put(setUserLocations(locationList ?? []));
    if (!payload?.skipActiveLocationUpdate) {
      yield put(setActiveLocation(location ?? {}));
    }
    yield put(setActiveSurvey(location ?? {}));
    yield put(getSubListAction());
    yield put(
      updateUserSuccess({ token: latestToken || token, user: userInfo.data }),
    );
    typeof onUpdateSuccess === "function" && onUpdateSuccess();
  } catch (e) {
    yield put(updateUserFailure(e));
  }
}

function loginErrorMessage(error) {
  const fromInterceptor = typeof error?.error === "string" ? error.error : null;
  const fromBody = error?.response?.data?.message;
  const raw =
    fromInterceptor ||
    (typeof fromBody === "string" ? fromBody : null) ||
    (fromBody && typeof fromBody === "object" && Object.keys(fromBody).length
      ? Object.values(fromBody).join(", ")
      : null);
  if (raw === "Invalid credentials") {
    return "Invalid email or password.";
  }
  return raw || "Login failed";
}

export function* login({ credentials, history }) {
  try {
    // FIXED: Changed from true to false to enable interceptors (for decryption)
    const loginResponse = yield call(
      api(null, null, false).post,
      `/api/employee/login`,
      credentials,
    );

    // Debug: Log the actual response structure

    // Handle both encrypted and non-encrypted responses
    const responseData = loginResponse?.data?.data || loginResponse?.data;
    const { access_token, expires_in, expires_at, refresh_ttl } =
      responseData || {};

    if (access_token) {
      // Keep the /api/employee/me call for access control logic
      // FIXED: Also enable interceptors for /me endpoint
      const userInfoResponse = yield call(
        api(access_token, null, false).post,
        `/api/employee/me`,
      );

      let user = overwritePerms(
        userInfoResponse?.data?.data || userInfoResponse?.data,
      );
      let locationList = populateList(user);
      let location = populateDefLocation(user);
      yield put(setUserLocations(locationList ?? []));
      yield put(setActiveLocation(location ?? {}));
      yield put(setActiveSurvey(location ?? {}));
      yield put(loginSuccess({ token: access_token, user: user }));

      // Store token with expiration info
      localStorage.setItem("token", access_token);
      if (expires_at) {
        localStorage.setItem("token_expires_at", expires_at);
      }
      if (expires_in) {
        localStorage.setItem("token_expires_in", expires_in);
      }

      yield put(getSubListAction());
      yield put(getMenuIndicatorsAction());

      let hasAllFeedAccess = allFeedAccess(user?.location);
      yield put(updateAllFeedAccess(hasAllFeedAccess));
      if (!hasAllFeedAccess) {
        yield put(
          setFeedFilter({
            target: "location",
            page: 1,
            limit: 10,
          }),
        );
      }

      if (user?.id) {
        Intercom({
          app_id: process.env.REACT_APP_INTERCOM_ID,
          email: user?.email,
          user_id: user?.id,
          user_hash: generateHmac(user?.id, process.env.REACT_APP_HMAC_SECRET),
          name: `${user?.first_name} ${user?.last_name}`,
          created_at: Date.now(),
        });
      }

      history.push(
        history.location.state &&
          history.location.state.referrer &&
          history.location.state.referrer.pathname !== "/"
          ? history.location.state.referrer.pathname
          : `/d/home`,
      );
    } else {
      console.error("❌ No access_token in response");
      console.error(
        "Response structure:",
        JSON.stringify(loginResponse, null, 2),
      );
      throw new Error("Login response missing access_token");
    }
  } catch (e) {
    console.error("❌ Login Error:", e);
    yield put(loginFailure(e));
    yield put(errorAlert(loginErrorMessage(e)));
  }
}

export function* logout() {
  try {
    const token = yield select(selectToken);
    const userId = yield select((state) => state?.AuthReducer?.user?.id);
    // Updated to match backend employee logout endpoint
    yield call(api(token).post, `/api/employee/logout`);
    clearFeedViewTracking(userId);
    clearFeedViewTracking();
    localStorage.clear();
    // shutdown();
    // eslint-disable-next-line no-restricted-globals
    if (location) location.replace("/");
  } catch (e) {
    yield put(logoutFailure(e));
    const userId = yield select((state) => state?.AuthReducer?.user?.id);
    clearFeedViewTracking(userId);
    clearFeedViewTracking();
    localStorage.clear();
    // eslint-disable-next-line no-restricted-globals
    if (location) location.replace("/");
  }
}

export function* sentResetLink({ payload, history }) {
  try {
    // FIXED: Enable interceptors for password reset (for decryption)
    const response = yield call(
      api(null, null, false).post,
      `/auth/password/email`,
      payload,
    );
    const data = response.data;
    if (data) {
      successAlert(data.message);
      sentResetLinkSuccess();
      history.push(
        history.location.state &&
          history.location.state.referrer &&
          history.location.state.referrer.pathname !== "/login"
          ? history.location.state.referrer.pathname
          : "/dealers",
      );
    } else if (history.location.pathname !== "/login")
      history.replace("/login");
  } catch (e) {
    yield put(sentResetLinkFailure(e));
    yield put(errorAlert("Error while login!"));
    localStorage.clear();
    if (history.location.pathname !== "/login") history.replace("/login");
  }
}

export function* forgotPassword({ credentials, onSuccess }) {
  const token = yield select(selectToken);
  try {
    const response = yield call(
      api(token).post,
      `/api/auth/employee/password/email`,
      credentials,
    );
    if (response && response?.code == 200) {
      yield put(forgotPasswordSuccess());
      yield put(successAlert(response.message));
      typeof onSuccess === "function" && onSuccess();
    }
  } catch (e) {
    yield put(forgotPasswordFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else yield put(errorAlert("Error while sending reset link!"));
  }
}

export function* resetPassword({ credentials, history }) {
  const token = yield select(selectToken);
  try {
    const response = yield call(
      api(token).post,
      `/api/auth/employee/password/reset`,
      credentials,
    );
    if (response && response?.code == 200) {
      yield put(resetPasswordSuccess());
      yield put(successAlert(response.message));
      if (history) {
        history.push(`/`);
      }
    }
  } catch (e) {
    yield put(resetPasswordFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else yield put(errorAlert("Error while resetting password!"));
  }
}

export function* getUserData({ payload }) {
  const token = yield select(selectToken);
  let query = payload ? QueryString.stringify(payload) : "";
  try {
    const response = yield call(
      api(token).get,
      `/api/admin-staff-dashboard?${query}`,
    );
    if (response && response?.code == 200) {
      yield put(getUserDataSuccess(response?.data));
    }
  } catch (e) {
    // yield put(getUserDataFailure());
    // if (typeof e === "string") yield put(errorAlert(e));
    // else if (typeof e.error === "string") yield put(errorAlert(e.error));
    // else yield put(errorAlert("Error while fetching data"));
  }
}

export function* getSubListData({ payload }) {
  const token = yield select(selectToken);
  try {
    const response = yield call(api(token).get, `/api/item-list`);
    yield put(getSubListSuccess(response?.data));
  } catch (e) {
    yield put(getSubListFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else yield put(errorAlert("Error while fetching data"));
  }
}

export function* setPasswordData({ credentials, history }) {
  const token = process.env.REACT_APP_CUSTOMER_API_KEY;
  try {
    const response = yield call(
      api(token).post,
      `/api/set-password/${credentials.id}`,
      credentials.data,
    );
    yield put(setPasswordSuccess());
    yield put(successAlert(response.message));
    if (history) {
      history.push(`/`);
    }
  } catch (e) {
    yield put(setPasswordFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else yield put(errorAlert("Error while setting password!"));
  }
}

export function* checkEmailSub({ payload }) {
  const token = process.env.REACT_APP_CUSTOMER_API_KEY;
  try {
    const { data } = yield call(
      api(token).post,
      `/api/check-subscribe-unsubscribe`,
      payload,
    );
    let isSubscribed = !data?.is_unsubscribe ?? false;
    yield put(checkEmailSubSuccess(isSubscribed));
  } catch (e) {
    yield put(checkEmailSubFailure());
  }
}

export function* updateEmailSub({ payload }) {
  const { onUpdateSuccess, mode } = payload;
  const token = process.env.REACT_APP_CUSTOMER_API_KEY;
  const apiConfig = {
    subscribe: `/api/subscribe-mail`,
    unsubscribe: `/api/unsubscribe-mail`,
  };

  try {
    const { data } = yield call(
      api(token).post,
      apiConfig[mode],
      payload?.data,
    );
    yield put(updateEmailSubSuccess());
    typeof onUpdateSuccess === "function" && onUpdateSuccess(mode);
  } catch (e) {
    yield put(updateEmailSubFailure());
  }
}

export function* getAppQR({ payload }) {
  const token = process.env.REACT_APP_CUSTOMER_API_KEY;
  try {
    const response = yield call(api(token).get, `/api/public/qr/app-store`);
    yield put(getAppQRSuccess(response?.data));
  } catch (e) {
    yield put(getAppQRFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else yield put(errorAlert("Error while fetching data"));
  }
}

export function* getMenuIndicatorsSaga() {
  const token = yield select(selectToken);
  try {
    const response = yield call(api(token).get, `/api/menu-indicator`);
    const body = response?.data ?? {};
    const nested = body?.data;
    const menuPayload =
      nested &&
      typeof nested === "object" &&
      !Array.isArray(nested) &&
      (nested.leave != null || nested.maintenance != null)
        ? nested
        : body;
    yield put(getMenuIndicatorsSuccess(menuPayload));
  } catch (e) {
    yield put(getMenuIndicatorsFailure());
  }
}

export function* checkAuthFlow() {
  yield takeLatest(CHECK, checkAuth);
}
export function* loginFlow() {
  yield takeLatest(LOGIN, login);
}
export function* logoutFlow() {
  yield takeLatest(LOGOUT, logout);
}
export function* sentResetLinkFlow() {
  yield takeLatest(SENT_RESET_PASSWORD, sentResetLink);
}
export function* forgotPasswordFlow() {
  yield takeEvery(FORGOT_PASSWORD, forgotPassword);
}
export function* resetPasswordFlow() {
  yield takeEvery(RESET_PASSWORD, resetPassword);
}
export function* getUserDataFlow() {
  yield takeEvery(GET_USERDATA, getUserData);
}
export function* getSubListFlow() {
  yield takeEvery(GET_SUBLIST, getSubListData);
}
export function* updateUserFlow() {
  yield takeEvery(UPDATE_USER, updateUserData);
}
export function* setPasswordFlow() {
  yield takeEvery(SET_PASSWORD, setPasswordData);
}
export function* checkEmailSubFlow() {
  yield takeEvery(CHECK_EMAIL_SUB, checkEmailSub);
}
export function* updateEmailSubFlow() {
  yield takeEvery(UPDATE_EMAIL_SUB, updateEmailSub);
}
export function* getAppQRFlow() {
  yield takeEvery(GET_APP_QR, getAppQR);
}
export function* getMenuIndicatorsFlow() {
  yield takeEvery(GET_MENU_INDICATORS, getMenuIndicatorsSaga);
}

export default function* rootAuthSaga() {
  yield all([
    loginFlow(),
    checkAuthFlow(),
    logoutFlow(),
    sentResetLinkFlow(),
    forgotPasswordFlow(),
    resetPasswordFlow(),
    getUserDataFlow(),
    getSubListFlow(),
    updateUserFlow(),
    setPasswordFlow(),
    checkEmailSubFlow(),
    updateEmailSubFlow(),
    getAppQRFlow(),
    getMenuIndicatorsFlow(),
  ]);
}
